import { PaginationState } from "@tanstack/react-table";

export type PaymentRefundStatus =
  | "pending"
  | "confirmed"
  | "error"
  | "rejected"
  | "skipped";
export type PaymentRefundStatusFilter =
  | ""
  | "pending"
  | "confirmed"
  | "failed"
  | "skipped";
export type PaymentRefundFailedStatusFilter = "" | "error" | "rejected";

export interface PaymentRefundRow {
  id: string;
  paymentId?: string;
  paymentReference: string;
  clientId?: string;
  clientName: string;
  clientDocument: string;
  createdAt: string;
  processedAt?: string | null;
  reason: string;
  reasonName: string;
  grossAmountVef: number;
  feeAmountVef: number;
  netAmountVef: number;
  referenceSmall: string;
  status: PaymentRefundStatus | string;
  statusName: string;
  errorMessage?: string;
}

export interface PaymentRefundIndicators {
  total: number;
  pending: number;
  confirmed: number;
  rejected: number;
  error: number;
  skipped: number;
  failed: number;
  totalNetAmountVef: number;
  skippedGrossAmountVef: number;
  pendingNetAmountVef: number;
  confirmedNetAmountVef: number;
  failedNetAmountVef: number;
}

export interface PaymentRefundListParams {
  page?: number;
  limit?: number;
  globalFilter?: string;
  startDate?: string;
  endDate?: string;
  status?: PaymentRefundStatusFilter;
  failedStatus?: PaymentRefundFailedStatusFilter;
}

export interface PaymentRefundListResult {
  data: PaymentRefundRow[];
  totalCount: number;
}

export interface PaymentRefundDestination {
  bankCode?: string;
  phone?: string;
  identificationType?: string;
  identificationNumber?: string;
  name?: string;
}

export interface PaymentRefundPaymentDetail {
  id?: string;
  createdAt?: string;
  amountUsd: number;
  amountVef: number;
  requestedAmountVef: number;
  reportedAmountVef: number;
  providerAmountVef: number;
  rate: number;
  paymentType: string;
  reference: string;
  referenceSmall: string;
  bankTxId: string;
  status: string;
  statusName: string;
  errorMessage?: string;
}

export interface PaymentRefundOperationPaymentDetail {
  id: string;
  date: string;
  paidAt?: string | null;
  amountUsd: number;
  amountVef: number;
  rate: number;
  remaining?: Record<string, unknown> | null;
  status: string;
  statusName: string;
  quotaNumber: string;
  quotaCode: string;
  paymentNumber: string;
  operationId?: string | null;
  operationReference: string;
  operationAmountUsd: number;
  operationFeeCount: number;
  operationIcon?: string | null;
  operationIconColor?: string | null;
}

export interface PaymentRefundDetail extends PaymentRefundRow {
  clientEmail: string;
  clientDocument: string;
  updatedAt?: string;
  provider: string;
  type: string;
  feeRate: number;
  feeFloorAmountVef: number;
  destination?: PaymentRefundDestination | null;
  attempt: number;
  transactionId: string;
  internalRef: string;
  statusCode: string;
  reference: string;
  bankTxId: string;
  movementReference: string;
  providerResponse?: unknown;
  payment?: PaymentRefundPaymentDetail | null;
  operationPayments: PaymentRefundOperationPaymentDetail[];
}

export type PaymentRefundPagination = PaginationState;

export const getEmptyPaymentRefundIndicators = (): PaymentRefundIndicators => ({
  total: 0,
  pending: 0,
  confirmed: 0,
  rejected: 0,
  error: 0,
  skipped: 0,
  failed: 0,
  totalNetAmountVef: 0,
  skippedGrossAmountVef: 0,
  pendingNetAmountVef: 0,
  confirmedNetAmountVef: 0,
  failedNetAmountVef: 0,
});

export const getPaymentRefundStatusLabel = (status?: string) => {
  switch (status) {
    case "pending":
      return "Pendiente";
    case "confirmed":
      return "Confirmado";
    case "error":
      return "Error";
    case "rejected":
      return "Rechazado";
    case "skipped":
      return "Omitido";
    default:
      return "Desconocido";
  }
};

export const getPaymentRefundReasonLabel = (reason?: string) => {
  switch (reason) {
    case "user_underpayment":
      return "Pago insuficiente";
    case "user_overpayment":
      return "Sobrepago";
    default:
      return reason || "Pago insuficiente";
  }
};
